import { useState, useEffect, useMemo } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { ordersLabels } from "@/i18n";
import { DeliveryMethodSelector } from "./DeliveryMethodSelector";
import { AddressDisplay } from "./AddressDisplay";
import { PurchaseStatus } from "./PurchaseStatus";
import { DeliveryFeePopup } from "./DeliveryFeePopup";
import ContractPopup from "./ContractPopup";
import InvoicePopup from "./InvoicePopup";
import { useTextColorClass } from "@/components/ui/text-coloring";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Check, Loader2, FileText, Receipt } from "lucide-react";
import { format } from "date-fns";
import { useUser } from "@/hooks/useUser";
import { useTransport } from "@/hooks/useTransport";
import { useAuth } from "@/hooks/useAuth";
import { useSettings } from "@/hooks/useSettings";
import { UserType } from "@/types/user";
import { toast } from "sonner";
import { ListingMyOrder } from "@/types/listing";
import { Transport } from "@/types/transport";
import { PaymentWithDetails } from "@/types/payment";
import { useContract } from "@/hooks/useContract";

interface CarDetailedInfoProps {
  listing?: ListingMyOrder;
  payment?: PaymentWithDetails;
  transport?: Transport | null;
}

export const CarDetailedInfo = ({
  listing,
  payment,
  transport,
}: CarDetailedInfoProps) => {
  const { language } = useLanguage();
  const o = ordersLabels(language);
  const db = o.deliveryBooking;
  const ds = o.deliveryStatus;
  const [deliveryMethod, setDeliveryMethod] = useState<"pickup" | "delivery">(
    "pickup",
  );
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string>("12:00");
  const [comment, setComment] = useState<string>("");
  const [isBooked, setIsBooked] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [wasRecentlyBooked, setWasRecentlyBooked] = useState(false);
  const [contractPopup, setContractPopup] = useState(false);
  const [invoicePopup, setInvoicePopup] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isStartingContractSignature, setIsStartingContractSignature] = useState(false);

  // Hooks for API calls
  const {
    createTransportAsync: createTransport,
    updateTransportAsync: updateTransport,
    isCreating: isCreatingTransport,
    isUpdating: isUpdatingTransport,
  } = useTransport();
  const { userDetails } = useUser();
  const { user } = useAuth();
  const isAdmin = user?.type === UserType.Admin;
  const { deliveryAddresses } = useSettings();
  const { getBankIdSignatureStartUrl } = useContract();

  // Determine if current user can book delivery:
  // 1. Admin can always book
  // 2. The actual buyer (winner) can book (check both user.id and userDetails._id)
  // 3. Users from the same company as the winner can book (for company accounts)
  // Backend enforces this, but we check here to show/hide the booking form
  const isActualBuyer = Boolean(
    listing?.winnerId &&
    (user?.id === listing.winnerId || userDetails?._id === listing.winnerId),
  );

  const isSameCompany = Boolean(
    userDetails?.companyId &&
    listing?.winnerCompanyId &&
    userDetails.companyId === listing.winnerCompanyId,
  );

  const isBuyer = isAdmin || isActualBuyer || isSameCompany;

  // Debug logging (remove in production)
  if (process.env.NODE_ENV === "development") {
  }

  const sellerPickup = listing?.sellerPickupAddress;
  const pickupAddress =
    sellerPickup &&
    sellerPickup.street?.trim() &&
    sellerPickup.postalCode?.trim() &&
    sellerPickup.city?.trim()
      ? {
          street: sellerPickup.street.trim(),
          postalCode: sellerPickup.postalCode.trim(),
          city: sellerPickup.city.trim(),
          country: (sellerPickup.country || "Sweden").trim(),
        }
      : undefined;

  const handleStartContractBankIdSignature = (contractId: string) => {
    setIsStartingContractSignature(true);
    void (async () => {
      try {
        const { isNativeCapacitorApp } = await import(
          "@/integrations/clients/carAuction/nativeAuth"
        );
        if (isNativeCapacitorApp()) {
          const { ContractService } = await import(
            "@/services/contract/contractService"
          );
          const continueUrl =
            await ContractService.getNativeBankIdSignatureContinueUrl(contractId);
          const { openBankIdInSystemBrowser } = await import(
            "@/lib/native/bankIdBrowser"
          );
          await openBankIdInSystemBrowser(continueUrl);
          return;
        }
        window.location.href = getBankIdSignatureStartUrl(contractId);
      } catch {
        setIsStartingContractSignature(false);
      }
    })();
  };

  // Initialize delivery method and status from transport data
  useEffect(() => {
    if (transport) {
      // Set delivery method based on transport type
      if (transport.type === "buyer_pickup") {
        setDeliveryMethod("pickup");
      } else if (transport.type === "buyer_delivery") {
        setDeliveryMethod("delivery");
      }

      // Set booking status
      if (
        transport.status === "requested" ||
        transport.status === "planned" ||
        transport.status === "in_progress"
      ) {
        setIsBooked(true);
        setWasRecentlyBooked(false);
      }

      // Set selected date and time if available
      if (transport.scheduledDate) {
        const scheduledDate = new Date(transport.scheduledDate);
        setSelectedDate(scheduledDate);
        setSelectedTime(scheduledDate.toTimeString().slice(0, 5));
      }

      // Set comment if available
      if (transport.comment) {
        setComment(transport.comment);
      }
    }
  }, [transport]);

  // Get delivery address from transport data if available, otherwise use DeliveryAddress from settings
  // IMPORTANT: Always prioritize address from delivery object (transport.toAddress) if it exists,
  // as admin may have booked with a different address than user's settings
  const effectiveDeliveryAddress = useMemo(() => {
    // PRIORITY 1: Check if there's a delivery transport with address (existing booking)
    // This is the most important - use the address from the delivery object itself
    if (
      transport &&
      transport.type === "buyer_delivery" &&
      transport.toAddress
    ) {
      // Parse address string (format: "street, postalCode city, country" or just "street")
      const addressString = transport.toAddress.trim();
      const addressParts = addressString.split(", ");

      // Try to parse full address format: "Street, PostalCode City, Country"
      if (addressParts.length >= 2) {
        const street = addressParts[0] || "";
        const cityAndZip = addressParts[1] || "";
        const country = addressParts[2] || "Sweden";

        // Try to extract postal code and city from "PostalCode City"
        const zipCityMatch = cityAndZip.match(/^(\d{3,5})\s+(.+)$/);
        if (zipCityMatch) {
          return {
            street,
            postalCode: zipCityMatch[1],
            city: zipCityMatch[2],
            country,
          };
        } else {
          // If no postal code found, treat entire string as city
          return {
            street,
            postalCode: "",
            city: cityAndZip,
            country,
          };
        }
      } else {
        // Single part - treat as street address (e.g., "Testvägen")
        // This handles cases where admin booked with just a street name
        return {
          street: addressString,
          postalCode: "",
          city: "",
          country: "Sweden",
        };
      }
    }

    // PRIORITY 2: If no existing transport, use DeliveryAddress from settings (default address)
    // Only use if address is complete (has street, postalCode, and city)
    if (
      deliveryMethod === "delivery" &&
      deliveryAddresses &&
      deliveryAddresses.length > 0
    ) {
      const defaultAddress =
        deliveryAddresses.find((a) => a.isDefault) || deliveryAddresses[0];
      if (
        defaultAddress &&
        defaultAddress.street &&
        defaultAddress.postalCode &&
        defaultAddress.city
      ) {
        return {
          street: defaultAddress.street,
          postalCode: defaultAddress.postalCode,
          city: defaultAddress.city,
          country: defaultAddress.country || "SE",
        };
      }
    }

    // PRIORITY 3: Fallback to user's profile address if no delivery addresses found
    // Only use if address is complete (has street, postalCode, and city)
    if (deliveryMethod === "delivery" && userDetails?.address) {
      const userAddress = userDetails.address;
      // Check if address is complete (has required fields)
      if (userAddress.street && userAddress.zipCode && userAddress.city) {
        return {
          street: userAddress.street,
          postalCode: userAddress.zipCode,
          city: userAddress.city,
          country: userAddress.country || "SE",
        };
      }
    }

    return null;
  }, [transport, deliveryMethod, deliveryAddresses, userDetails]);

  const handleConfirm = async () => {
    if (!listing) {
      toast.error(db.noListingFound);
      return;
    }

    if (!selectedDate || !selectedTime) {
      toast.error(db.selectDateFirst);
      return;
    }

    // Validate minimum 2 days ahead
    const deliveryDate = new Date(selectedDate);
    const now = new Date();
    const minDate = new Date(now);
    minDate.setDate(minDate.getDate() + 2);
    minDate.setHours(0, 0, 0, 0);

    if (deliveryDate < minDate) {
      toast.error(db.minDateError);
      return;
    }

    // For delivery, check if address is available
    if (deliveryMethod === "delivery" && !effectiveDeliveryAddress) {
      toast.error(db.addressRequiredToast);
      return;
    }

    setIsBooking(true);

    try {
      const scheduledTime = new Date(selectedDate);
      const [hours, minutes] = selectedTime.split(":");
      scheduledTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      const transportType: "buyer_pickup" | "buyer_delivery" =
        deliveryMethod === "delivery" ? "buyer_delivery" : "buyer_pickup";
      //  TODO: Put BILENIA adress in const file and read from it
      const fromAddress = "Bilenia Lager, Hägersten, Stockholm";
      const toAddress =
        deliveryMethod === "delivery"
          ? effectiveDeliveryAddress?.street || ""
          : "Bilenia Lager, Hägersten, Stockholm";

      // Prepare transport data for buyer delivery/pickup
      const transportData = {
        listingId: listing.id,
        type: transportType,
        fromAddress,
        toAddress,
        scheduledDate: scheduledTime.toISOString(),
        comment: comment || "",
      };

      // Check if we're editing an existing transport
      if (isEditMode && transport) {
        // Update existing transport
        const updateData = {
          id: transport.id,
          listingId: listing.id,
          type: transportType,
          scheduledDate: scheduledTime.toISOString(),
          comment: transportData.comment,
          ...(deliveryMethod === "delivery" &&
            effectiveDeliveryAddress && {
              address: `${effectiveDeliveryAddress.street}, ${effectiveDeliveryAddress.postalCode} ${effectiveDeliveryAddress.city}, ${effectiveDeliveryAddress.country}`,
            }),
        };

        await updateTransport(updateData);
      } else {
        // Create new transport
        const createData: {
          listingId: string;
          type: "buyer_pickup" | "buyer_delivery";
          fromAddress: string;
          toAddress: string;
          scheduledDate: string;
          comment: string;
        } = {
          listingId: listing.id,
          type: transportType,
          fromAddress,
          toAddress,
          scheduledDate: scheduledTime.toISOString(),
          comment: transportData.comment,
        };

        await createTransport(createData);
      }

      const method = deliveryMethod === "pickup" ? db.pickup : db.homeDelivery;

      if (isEditMode) {
        toast.success(
          db.bookingUpdated
            .replace("{method}", method)
            .replace("{date}", format(scheduledTime, "yyyy-MM-dd"))
            .replace("{time}", selectedTime),
        );
        setIsEditMode(false);
      } else {
        toast.success(
          db.bookingSuccess
            .replace("{method}", method)
            .replace("{date}", format(scheduledTime, "yyyy-MM-dd"))
            .replace("{time}", selectedTime),
        );
        setIsBooked(true);
        setWasRecentlyBooked(false);
      }
    } catch (error: unknown) {
      const err = error as {
        message?: string;
        response?: { data?: { message?: string } };
      };
      const message = err?.response?.data?.message ?? err?.message ?? "";
      const isOnlyBuyerError =
        message.includes("Endast köparen") ||
        message.includes("Only the buyer");
      toast.error(isOnlyBuyerError ? db.onlyBuyerCanBook : db.bookingError);
    } finally {
      setIsBooking(false);
    }
  };

  const canBook =
    !!selectedDate &&
    !!selectedTime &&
    (deliveryMethod === "pickup" || effectiveDeliveryAddress);
  const needsAddress =
    deliveryMethod === "delivery" && !effectiveDeliveryAddress;

  // Get delivery status from transport data or state
  const getDeliveryStatus = () => {
    if (transport) {
      switch (transport.status) {
        case "not_started":
          return db.notBooked;
        case "requested":
          return ds.requestSent;
        case "planned":
          return ds.confirmed;
        case "in_progress":
          return ds.inProgress;
        case "completed":
          return ds.delivered;
        default:
          break;
      }
    }

    // Optimistic status after save, before listing refetch
    if (isBooked) {
      return ds.requestSent;
    }

    return db.notBooked;
  };

  const deliveryStatus = getDeliveryStatus();

  // Show booking confirmation message when booked from existing data or after successful booking
  const showBookingConfirmation = isBooked;

  // Check if delivery is completed (non-editable)
  const isDeliveryCompleted = transport?.status === "completed";

  // Check if we should show the booking form (only if no transport exists or transport is not completed)
  const shouldShowBookingForm = !transport || transport.status !== "completed";

  const contractSignatureStatusLabel =
    payment?.contract?.buyer_signature_status === "signed"
      ? o.contractPopup?.signatureSigned ?? "Signerat"
      : payment?.contract?.buyer_signature_status === "requested"
        ? o.contractPopup?.signatureRequested ?? "Signering begärd"
        : payment?.contract?.buyer_signature_status === "declined"
          ? o.contractPopup?.signatureDeclined ?? "Signering misslyckad"
          : o.contractPopup?.signatureNotRequested ?? "Ingen signering begärd";

  // Check if any values have changed from the original transport data
  const hasChanges = useMemo(() => {
    if (!transport) return true; // If no original data, any input is a change

    const originalDate = transport.scheduledDate
      ? new Date(transport.scheduledDate)
      : undefined;
    const originalTime = transport.scheduledDate
      ? new Date(transport.scheduledDate).toTimeString().slice(0, 5)
      : undefined;

    const dateChanged = selectedDate?.getTime() !== originalDate?.getTime();
    const timeChanged = selectedTime !== originalTime;
    const commentChanged = comment !== (transport.comment || "");

    return dateChanged || timeChanged || commentChanged;
  }, [transport, selectedDate, selectedTime, comment]);

  return (
    <div className="space-y-6">
      <PurchaseStatus paymentDate={payment?.paid_date} />

      {/* Show booking form - if user sees this listing in orders, they have permission to book */}
      {showBookingConfirmation && !isEditMode && shouldShowBookingForm ? (
        // Show existing booking summary
        <div className="space-y-4">
          {/* Determine card color based on status */}
          {(() => {
            const isRequested = transport?.status === "requested";
            const isPlanned = transport?.status === "planned";
            const isCompleted = transport?.status === "completed";

            // Blue for REQUESTED, green for PLANNED/COMPLETED
            const cardBg = isRequested
              ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700"
              : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700";
            const cardText = isRequested
              ? "text-blue-700 dark:text-blue-300"
              : "text-green-700 dark:text-green-300";
            const detailText = isRequested
              ? "text-blue-600 dark:text-blue-400"
              : "text-green-600 dark:text-green-400";

            return (
              <div className={`p-4 rounded-lg border ${cardBg}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className={`text-base font-medium ${cardText}`}>
                    {isCompleted
                      ? deliveryMethod === "pickup"
                        ? db.pickupDelivered
                        : db.homeDeliveryDelivered
                      : isPlanned
                        ? deliveryMethod === "pickup"
                          ? db.pickupConfirmed
                          : db.homeDeliveryConfirmed
                        : deliveryMethod === "pickup"
                          ? db.pickupBooked
                          : db.homeDeliveryBooked}
                  </div>
                  <Badge
                    variant="outline"
                    className={`${
                      deliveryStatus === ds.delivered
                        ? "bg-green-100 text-green-800 border-green-200"
                        : deliveryStatus === ds.confirmed
                          ? "bg-green-100 text-green-800 border-green-200"
                          : deliveryStatus === ds.requestSent
                            ? "bg-blue-100 text-blue-800 border-blue-200"
                            : deliveryStatus === ds.inProgress
                              ? "bg-orange-100 text-orange-800 border-orange-200"
                              : "bg-red-100 text-red-800 border-red-200"
                    }`}
                  >
                    {deliveryStatus}
                  </Badge>
                </div>

                {/* Delivery Method */}
                <div className={`text-sm ${detailText} mb-2`}>
                  <span className="font-medium">{db.deliveryMethod}</span>{" "}
                  {deliveryMethod === "pickup" ? db.pickup : db.homeDelivery}
                </div>

                {/* Scheduled Time */}
                {selectedDate && selectedTime && (
                  <div className={`text-sm ${detailText} mb-2`}>
                    <span className="font-medium">{db.scheduled}</span>{" "}
                    {format(selectedDate, "yyyy-MM-dd")} kl. {selectedTime}
                  </div>
                )}

                {/* Delivery Address for delivery method */}
                {deliveryMethod === "delivery" && effectiveDeliveryAddress && (
                  <div className={`text-sm ${detailText} mb-2`}>
                    <span className="font-medium">{db.deliveryAddress}</span>{" "}
                    {effectiveDeliveryAddress.street}
                  </div>
                )}

                {/* Pickup Address for pickup method */}
                {deliveryMethod === "pickup" &&
                  (pickupAddress ? (
                    <div className={`text-sm ${detailText} mb-2`}>
                      <span className="font-medium">{db.pickupAddress}</span>{" "}
                      {pickupAddress.street}, {pickupAddress.postalCode}{" "}
                      {pickupAddress.city}
                      {pickupAddress.country
                        ? `, ${pickupAddress.country}`
                        : ""}
                    </div>
                  ) : (
                    <div className="p-3 mb-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <p className={`text-sm ${detailText}`}>
                        <strong>{db.sellerPickupMissing}</strong>{" "}
                        {db.sellerPickupMissingSupport}
                      </p>
                    </div>
                  ))}

                {/* Comment */}
                {comment && (
                  <div className={`text-sm ${detailText} mb-3`}>
                    <span className="font-medium">{db.comment}</span> {comment}
                  </div>
                )}

                {!isDeliveryCompleted && (
                  <Button
                    variant="outline"
                    onClick={() => setIsEditMode(true)}
                    className={`w-full ${
                      isRequested
                        ? "border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-600 dark:text-blue-300 dark:hover:bg-blue-800"
                        : "border-green-300 text-green-700 hover:bg-green-100 dark:border-green-600 dark:text-green-300 dark:hover:bg-green-800"
                    }`}
                  >
                    {db.changeBooking}
                  </Button>
                )}
              </div>
            );
          })()}
        </div>
      ) : shouldShowBookingForm ? (
        // Show edit form (either for new booking or editing existing)
        <div className="space-y-4">
          <DeliveryMethodSelector
            value={deliveryMethod}
            onChange={setDeliveryMethod}
            onDateChange={setSelectedDate}
            onTimeChange={setSelectedTime}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            isEditMode={isEditMode}
          />

          {deliveryMethod === "pickup" &&
            (pickupAddress ? (
              <AddressDisplay type="pickup" address={pickupAddress} />
            ) : (
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>{db.sellerPickupMissing}</strong>{" "}
                  {db.sellerPickupMissingSupport}
                </p>
              </div>
            ))}

          {deliveryMethod === "delivery" && (
            <>
              {effectiveDeliveryAddress ? (
                <>
                  <AddressDisplay
                    type="delivery"
                    address={effectiveDeliveryAddress}
                  />
                  <DeliveryFeePopup
                    deliveryFee={listing?.deliveryFee || null}
                    listingId={listing?.id || ""}
                    isAdmin={isAdmin}
                  />
                </>
              ) : (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    <strong>{db.addressMissing}</strong> {db.addressMissingText}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Comment field */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{db.commentOptional}</label>
            <Textarea
              placeholder={db.commentPlaceholder}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-[80px]"
            />
          </div>

          {/* Boka/Updatera button */}
          <div className="gap-2">
            <Button
              variant="default"
              onClick={handleConfirm}
              className="flex items-center gap-2"
              disabled={
                !canBook ||
                isBooking ||
                isCreatingTransport ||
                isUpdatingTransport ||
                (isEditMode && !hasChanges)
              }
            >
              {isBooking || isCreatingTransport || isUpdatingTransport ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {isEditMode ? db.updating : db.booking}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  {isEditMode
                    ? deliveryMethod === "pickup"
                      ? db.updatePickup
                      : db.updateHomeDelivery
                    : deliveryMethod === "pickup"
                      ? db.bookPickup
                      : db.bookHomeDelivery}
                </>
              )}
            </Button>
            {!selectedDate || !selectedTime ? (
              <span className="text-xs text-muted-foreground">
                {db.selectDateFirst}
              </span>
            ) : needsAddress ? (
              <span className="text-xs text-muted-foreground">
                {db.addAddressInProfile}
              </span>
            ) : isEditMode && !hasChanges ? (
              <span className="text-xs text-muted-foreground">
                {db.noChanges}
              </span>
            ) : null}
          </div>

          {/* Cancel edit button when in edit mode */}
          {isEditMode && (
            <Button
              onClick={() => {
                setIsEditMode(false);
                // Reset to original values from transport data
                if (transport) {
                  if (transport.scheduledDate) {
                    const scheduledDate = new Date(transport.scheduledDate);
                    setSelectedDate(scheduledDate);
                    setSelectedTime(scheduledDate.toTimeString().slice(0, 5));
                  }
                  if (transport.comment) {
                    setComment(transport.comment);
                  } else {
                    setComment("");
                  }
                }
              }}
              className="w-full"
            >
              {o.delivery.cancel}
            </Button>
          )}
        </div>
      ) : (
        // Show completed delivery message (non-editable)
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700">
            <div className="flex items-center justify-between mb-3">
              <div className="text-base font-medium text-green-700 dark:text-green-300">
                {deliveryMethod === "pickup"
                  ? db.pickupDelivered
                  : db.homeDeliveryDelivered}
              </div>
              <Badge
                variant="outline"
                className="text-green-700 border-green-300"
              >
                {ds.delivered}
              </Badge>
            </div>

            {/* Delivery Method */}
            <div className="text-sm text-green-600 dark:text-green-400 mb-2">
              <span className="font-medium">{db.deliveryMethod}</span>{" "}
              {deliveryMethod === "pickup" ? db.pickup : db.homeDelivery}
            </div>

            {/* Scheduled Time */}
            {transport?.scheduledDate && (
              <div className="text-sm text-green-600 dark:text-green-400 mb-2">
                <span className="font-medium">{db.deliveredLabel}</span>{" "}
                {format(new Date(transport.scheduledDate), "yyyy-MM-dd")} kl.{" "}
                {new Date(transport.scheduledDate).toTimeString().slice(0, 5)}
              </div>
            )}

            {/* Delivery Address for delivery method */}
            {deliveryMethod === "delivery" && effectiveDeliveryAddress && (
              <div className="text-sm text-green-600 dark:text-green-400 mb-2">
                <span className="font-medium">{db.deliveryAddress}</span>{" "}
                {effectiveDeliveryAddress.street}
                {effectiveDeliveryAddress.postalCode &&
                  effectiveDeliveryAddress.city && (
                    <>
                      , {effectiveDeliveryAddress.postalCode}{" "}
                      {effectiveDeliveryAddress.city}
                    </>
                  )}
              </div>
            )}

            {/* Pickup Address for pickup method */}
            {deliveryMethod === "pickup" &&
              (pickupAddress ? (
                <div className="text-sm text-green-600 dark:text-green-400 mb-2">
                  <span className="font-medium">{db.pickupAddress}</span>{" "}
                  {pickupAddress.street}, {pickupAddress.postalCode}{" "}
                  {pickupAddress.city}
                  {pickupAddress.country ? `, ${pickupAddress.country}` : ""}
                </div>
              ) : (
                <div className="text-sm text-amber-700 dark:text-amber-300 mb-2 p-2 rounded border border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-950/30">
                  <strong>{db.sellerPickupMissing}</strong>{" "}
                  {db.sellerPickupMissingSupport}
                </div>
              ))}

            {/* Comment */}
            {transport?.comment && (
              <div className="text-sm text-green-600 dark:text-green-400 mb-3">
                <span className="font-medium">{db.comment}</span>{" "}
                {transport.comment}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contract and Invoice buttons */}
      {payment && (
        <div className="mt-6 pt-4 border-t">
          <h4 className="text-sm font-medium mb-3">{db.documents}</h4>
          <div className="flex flex-col md:flex-row gap-3">
            <Button
              variant="outline"
              size="lg"
              className="flex-1 gap-3 text-sm md:text-base"
              disabled={!payment.contract}
              onClick={() => setContractPopup(true)}
            >
              <FileText className="w-4 h-4 md:w-5 md:h-5" />
              {db.viewContract}
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="flex-1 gap-3 text-sm md:text-base"
              disabled={!payment.invoice}
              onClick={() => setInvoicePopup(true)}
            >
              <Receipt className="w-4 h-4 md:w-5 md:h-5" />
              {db.viewInvoice}
            </Button>
          </div>

          {payment.contract && (
            <div className="mt-3 rounded-lg border bg-muted/30 p-3 text-sm">
              <div className="font-medium">
                {o.contractPopup?.signatureStatus ?? "Signeringsstatus"}: {contractSignatureStatusLabel}
              </div>
              {payment.contract.buyer_signature_status === "signed" &&
                payment.contract.buyer_signature_method === "bankid_idura" && (
                <div className="mt-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-green-900">
                  <div className="flex items-center gap-2 font-medium">
                    <img
                      src="/assets/BankID_logo.png"
                      alt={o.contractPopup?.methodBankId ?? "BankID"}
                      className="h-4 w-auto object-contain"
                    />
                    {o.contractPopup?.contractSignedBankIdViaIdura ??
                      "Kontraktet är signerat med BankID via Idura"}
                  </div>
                  <div className="mt-1 text-xs text-green-800">
                    {o.contractPopup?.contractSignedPdfReplacedUpload ??
                      "Det uppladdade kontraktet har ersatts med den signerade PDF-versionen."}
                  </div>
                </div>
              )}
              {payment.contract.buyer_signature_method && (
                <div className="text-muted-foreground mt-1">
                  {o.contractPopup?.signatureMethod ?? "Metod"}:{" "}
                  {payment.contract.buyer_signature_method === "bankid_idura"
                    ? `${o.contractPopup?.methodBankId ?? "BankID"} via Idura`
                    : payment.contract.buyer_signature_method === "physical_id_check"
                      ? o.contractPopup?.methodPhysical ?? "Fysiskt signerad med ID-kontroll"
                      : payment.contract.buyer_signature_method === "signed_externally"
                        ? o.contractPopup?.methodExternal ?? "Signerad utanför systemet"
                        : payment.contract.buyer_signature_method === "no_signature_required"
                          ? o.contractPopup?.methodNotRequired ?? "Signering behövs ej"
                        : o.contractPopup?.methodOther ?? "Annan bekräftad metod"}
                </div>
              )}
              {payment.contract.buyer_signed_by_name && (
                <div className="text-muted-foreground mt-1">
                  {o.contractPopup?.signatureSignedBy ?? "Signerad av"}:{" "}
                  {payment.contract.buyer_signed_by_name}
                </div>
              )}
              {payment.contract.buyer_signed_at && (
                <div className="text-muted-foreground mt-1">
                  {o.contractPopup?.signatureDate ?? "Signeringsdatum"}:{" "}
                  {new Date(payment.contract.buyer_signed_at).toLocaleString("sv-SE")}
                </div>
              )}
              {userDetails?.bankIdStatus === "approved" &&
                payment.contract.contract_file &&
                payment.contract.buyer_signature_status !== "signed" && (
                <Button
                  className="mt-3 gap-2"
                  onClick={() => {
                    handleStartContractBankIdSignature(payment.contract!.id);
                  }}
                >
                  <img
                    src="/assets/BankID_logo.png"
                    alt="BankID"
                    className="h-4 w-auto object-contain"
                  />
                  {o.contractPopup?.signWithBankId ?? "Signera med BankID"}
                </Button>
              )}
            </div>
          )}

          {/* Error messages for missing documents */}
          {(!payment.contract || !payment.invoice) && (
            <div className="text-center mt-3">
              <p className="text-red-500 text-sm">
                {!payment.contract && !payment.invoice
                  ? db.noInvoiceOrContract
                  : !payment.contract
                    ? db.noContract
                    : db.noInvoice}
              </p>
            </div>
          )}
        </div>
      )}

      {isStartingContractSignature && (
        <div className="fixed inset-0 z-[120] bg-background/90 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 rounded-2xl border bg-background px-8 py-7 shadow-xl">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <div className="text-center">
              <div className="text-base font-semibold">
                {o.contractPopup?.redirectingToBankId ?? "Öppnar BankID-signering"}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {o.contractPopup?.redirectingToBankIdDescription ??
                  "Vänta medan vi skickar dig vidare till Idura för att signera kontraktet."}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contract Popup */}
      {contractPopup && payment && (
        <ContractPopup
          contract={{
            contract: payment.contract!,
            listing: {
              id: listing?.id || "",
              title: listing?.title || "",
              regNumber: listing?.vehicleDetails?.regNumber || "",
              make: listing?.vehicleDetails?.make || "",
              model: listing?.vehicleDetails?.model || "",
              year: String(listing?.vehicleDetails?.year || ""),
              color: listing?.vehicleDetails?.color || "",
              mileage: listing?.vehicleDetails?.mileage || 0,
              campaignId: listing?.campaignId || "",
              status: listing?.status || "",
            },
            customer: {
              _id: userDetails?._id || "",
              email: userDetails?.email || "",
              role: userDetails?.role || "individual",
              firstName: userDetails?.firstName || "",
              lastName: userDetails?.lastName || "",
            },
          }}
          onClose={() => setContractPopup(false)}
        />
      )}

      {/* Invoice Popup */}
      {invoicePopup && payment && (
        <InvoicePopup
          invoice={{
            invoice: payment.invoice!,
            listing: {
              id: listing?.id || "",
              title: listing?.title || "",
              regNumber: listing?.vehicleDetails?.regNumber || "",
              make: listing?.vehicleDetails?.make || "",
              model: listing?.vehicleDetails?.model || "",
              year: String(listing?.vehicleDetails?.year || ""),
              color: listing?.vehicleDetails?.color || "",
              mileage: listing?.vehicleDetails?.mileage || 0,
              campaignId: listing?.campaignId || "",
              status: listing?.status || "",
            },
            payment: {
              ...payment,
              due_date: payment?.invoice?.due_date || null,
            },
            customer: {
              _id: userDetails?._id || "",
              email: userDetails?.email || "",
              role: userDetails?.role || "individual",
              firstName: userDetails?.firstName || "",
              lastName: userDetails?.lastName || "",
            },
          }}
          onClose={() => setInvoicePopup(false)}
        />
      )}
    </div>
  );
};

